// Modelos y helpers de horarios — réplica del flujo unificado de la app Flutter.
// Referencia: flutterUXS/lib/backend/api_requests/models/schedule_models.dart
//             flutterUXS/lib/backend/api_requests/models/slot_grouping.dart
//
// El backend (`get_professional_schedule_today_to_three_days_after` y
// `create_include_consultation_by_user_v2`) devuelve slots atómicos (típicamente
// 20 min). El frontend agrupa según la duración elegida (1:1 o 2:1) usando
// `groupSlotsByDuration`, exactamente como la app.

import { canSeeTestProfessionals, isTestProfessional } from '../utils/testProfessional';

export const BookingMode = Object.freeze({
  IMMEDIATE: 'immediate',
  ON_DEMAND: 'on_demand',
});

// Mínimo de horas de anticipación para agendar/solicitar una consulta.
// Debe coincidir con el backend (`ir.config_parameter
// daisies_previous_hours_appointments`, default 6); el endpoint
// `create_include_consultation_by_user` rechaza con "Must schedule 6 hours
// before appointment" cuando no se cumple. Lo replicamos en el cliente para no
// mostrar (ni enviar) slots pasados o demasiado próximos.
export const MIN_BOOKING_LEAD_HOURS = 6;

// Construye el Date local de inicio del slot a partir de la fecha del día
// (YYYY-MM-DD) y la hora de inicio (HH:MM). Devuelve null si falta/está malformada.
// Nota tz: asume que la hora del slot está en la zona local del usuario (caso
// real: usuarias y plataforma en horario de México), igual que `formatDayFull`.
export const slotStartDate = (dateString, hourStart) => {
  if (typeof dateString !== 'string' || !dateString) return null;
  if (typeof hourStart !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(hourStart);
  if (!m) return null;
  const base = new Date(dateString + 'T00:00:00');
  if (Number.isNaN(base.getTime())) return null;
  base.setHours(Number(m[1]), Number(m[2]), 0, 0);
  return base;
};

// True si el slot cumple el mínimo de anticipación: su inicio cae en o después
// de `now + minLeadHours`. Filtra de un solo golpe los slots pasados y los que
// están dentro del mínimo (no agendables por el backend).
export const isSlotBookable = (
  dateString,
  hourStart,
  { minLeadHours = MIN_BOOKING_LEAD_HOURS, now = new Date() } = {}
) => {
  const start = slotStartDate(dateString, hourStart);
  if (!start) return false;
  const threshold = now.getTime() + minLeadHours * 3600 * 1000;
  return start.getTime() >= threshold;
};

// Filtra una lista de slots (cualquier shape con `hourStart`) de un día por el
// mínimo de anticipación.
export const filterBookableSlots = (dateString, slots, opts) =>
  (Array.isArray(slots) ? slots : []).filter(s => isSlotBookable(dateString, s?.hourStart, opts));

export const ScheduleType = Object.freeze({
  AVAILABLE: 'available',
  PREFERENTIAL: 'preferential',
  PRIORITY: 'priority',
});

const parseBookingMode = (raw) => {
  if (raw === 'on_demand') return BookingMode.ON_DEMAND;
  return BookingMode.IMMEDIATE;
};

const parseScheduleType = (raw) => {
  if (raw === 'preferential') return ScheduleType.PREFERENTIAL;
  if (raw === 'priority') return ScheduleType.PRIORITY;
  return ScheduleType.AVAILABLE;
};

// Convierte un slot atómico del backend en un DoctorSlot normalizado.
// Tolera variaciones de naming entre endpoints (hour vs hour_start, available vs is_locked).
export const parseDoctorSlot = (raw) => {
  if (!raw || typeof raw !== 'object') return null;

  // Doctoras de prueba: ocultas salvo en localhost o usuario interno.
  const showTest = canSeeTestProfessionals();

  // Si el slot llegaba con doctoras pero TODAS eran is_test_professional → descartar slot.
  // Evita renderizar "0 doctoras disponibles" después de filtrar.
  if (!showTest && Array.isArray(raw.professional_ids) && raw.professional_ids.length > 0) {
    const realProfs = raw.professional_ids.filter(p => !isTestProfessional(p));
    if (realProfs.length === 0) return null;
  }

  const rawConsultation = raw.consultation;
  let consultation = null;
  let isOccupied = false;
  if (rawConsultation && typeof rawConsultation === 'object' && !Array.isArray(rawConsultation)) {
    consultation = rawConsultation;
    isOccupied = Object.keys(rawConsultation).length > 0;
  }

  const isLocked = Object.prototype.hasOwnProperty.call(raw, 'available')
    ? !raw.available
    : !!raw.is_locked;

  const rawSource = typeof raw.source === 'string' ? raw.source : '';
  const source = rawSource && rawSource.length > 0
    ? rawSource
    : (isLocked ? 'support' : 'professional');

  const hourStart = (typeof raw.hour === 'string' && raw.hour)
    ? raw.hour
    : (typeof raw.hour_start === 'string' ? raw.hour_start : (typeof raw.init === 'string' ? raw.init : ''));
  const hourStop = (typeof raw.hour_stop === 'string' && raw.hour_stop)
    ? raw.hour_stop
    : (typeof raw.end === 'string' ? raw.end : '');

  const slotId = (typeof raw.id === 'number') ? raw.id
    : (typeof raw.index === 'number' ? raw.index
      : (raw.id != null ? Number(raw.id) : null));

  return {
    hourStart,
    hourStop,
    isExtra: !!raw.is_extra,
    isLocked,
    source,
    lockReason: typeof raw.lock_reason === 'string' ? raw.lock_reason : '',
    isOccupied,
    consultation,
    scheduleType: parseScheduleType(raw.schedule_type),
    bookingMode: parseBookingMode(raw.booking_mode),
    slotId: Number.isFinite(slotId) ? slotId : null,
    professionalIds: Array.isArray(raw.professional_ids)
      ? (showTest ? raw.professional_ids : raw.professional_ids.filter(p => !isTestProfessional(p)))
      : [],
    raw,
  };
};

// Day en respuesta del backend (week_day, date, schedule_available).
export const parseDaySchedule = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const slotsArr = Array.isArray(raw.schedule_available)
    ? raw.schedule_available
    : (Array.isArray(raw.schedule) ? raw.schedule : []);
  const slots = slotsArr
    .map(parseDoctorSlot)
    .filter(Boolean);
  return {
    date: typeof raw.date === 'string' ? raw.date : '',
    weekDay: typeof raw.week_day === 'string' ? raw.week_day : (raw.day || ''),
    slots,
    raw,
  };
};

// Respuesta unificada (`$.result`) del endpoint by-doctor o by-date.
// Se intenta normalizar tanto el envoltorio `{success, data: [...]}` como
// el formato legacy donde `result` es directamente la lista de días.
export const parseWeekScheduleResponse = (resultEnvelope) => {
  if (!resultEnvelope) return { success: false, slotDurationMinutes: 0, days: [], hasAvailability: false };

  // Algunos endpoints envuelven la lista en `data`, otros la devuelven directa.
  const daysRaw = Array.isArray(resultEnvelope?.data)
    ? resultEnvelope.data
    : (Array.isArray(resultEnvelope) ? resultEnvelope : []);

  const days = daysRaw.map(parseDaySchedule).filter(Boolean);

  const slotDurationMinutes = Number.isFinite(resultEnvelope.slot_duration_minutes)
    ? Number(resultEnvelope.slot_duration_minutes)
    : 0;

  const hasAvailability = typeof resultEnvelope.has_availability === 'boolean'
    ? resultEnvelope.has_availability
    : days.some(d => d.slots.some(s => !s.isLocked && !s.isOccupied));

  return {
    success: !!(resultEnvelope.success ?? true),
    slotDurationMinutes,
    days,
    hasAvailability,
  };
};

// Agrupa slots atómicos en BookingSlots según `targetDurationMinutes`.
// Reglas (idénticas a slot_grouping.dart):
//   - Excluye slots ocupados y de tipo `priority`.
//   - Si target == atomic → 1:1.
//   - Si target == atomic * 2 → agrupa pares contiguos que compartan
//     `isExtra` y `source`. Si cualquiera es on_demand, el par también.
//     Si cualquiera es locked, el par es locked.
//   - Slots huérfanos se descartan.
//   - Cualquier otra razón cae a 1:1 con warning.
export const groupSlotsByDuration = ({ slots, targetDurationMinutes, atomicSlotMinutes }) => {
  if (!Array.isArray(slots) || slots.length === 0) return [];

  const available = slots.filter(s => !s.isOccupied && s.scheduleType !== ScheduleType.PRIORITY);

  // Caso "by-date": el backend ya devuelve slots con la duración correcta y
  // NO emite `slot_duration_minutes`. En ese escenario no hay agrupación que
  // hacer — cada slot atómico ES un BookingSlot. La versión anterior devolvía
  // array vacío cuando faltaba info, lo cual ocultaba todos los horarios.
  if (!Number.isFinite(targetDurationMinutes) || targetDurationMinutes <= 0 ||
      !Number.isFinite(atomicSlotMinutes) || atomicSlotMinutes <= 0) {
    return available.map(toOneToOne);
  }

  if (targetDurationMinutes === atomicSlotMinutes) {
    return available.map(toOneToOne);
  }

  if (targetDurationMinutes === atomicSlotMinutes * 2) {
    return groupPairs(available);
  }

  // ratio inesperado — log y fallback 1:1
  // eslint-disable-next-line no-console
  console.warn(`[scheduleModels] groupSlotsByDuration: ratio no soportado (target=${targetDurationMinutes}, atomic=${atomicSlotMinutes}); fallback a 1:1`);
  return available.map(toOneToOne);
};

const toOneToOne = (s) => ({
  hourStart: s.hourStart,
  hourStop: s.hourStop,
  isExtra: s.isExtra,
  isLocked: s.isLocked,
  source: s.source,
  components: [s],
  scheduleType: s.scheduleType,
  bookingMode: s.bookingMode,
  slotId: s.slotId,
  professionalIds: s.professionalIds || [],
});

const groupPairs = (available) => {
  const result = [];
  let i = 0;
  while (i < available.length - 1) {
    const a = available[i];
    const b = available[i + 1];
    const contiguous = a.hourStop && a.hourStop === b.hourStart;
    const sameKind = a.isExtra === b.isExtra && a.source === b.source;
    if (contiguous && sameKind) {
      const pairBookingMode = (a.bookingMode === BookingMode.ON_DEMAND || b.bookingMode === BookingMode.ON_DEMAND)
        ? BookingMode.ON_DEMAND
        : BookingMode.IMMEDIATE;
      // Profesionales: si ambos comparten al menos una doctora, ese subconjunto es la oferta del par.
      const mergedProfs = intersectProfessionals(a.professionalIds, b.professionalIds);
      result.push({
        hourStart: a.hourStart,
        hourStop: b.hourStop,
        isExtra: a.isExtra,
        isLocked: a.isLocked || b.isLocked,
        source: a.source,
        components: [a, b],
        scheduleType: a.scheduleType,
        bookingMode: pairBookingMode,
        slotId: a.slotId,
        professionalIds: mergedProfs.length > 0 ? mergedProfs : (a.professionalIds || []),
      });
      i += 2;
    } else {
      i += 1;
    }
  }
  return result;
};

const intersectProfessionals = (a = [], b = []) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return [];
  if (a.length === 0 || b.length === 0) return [];
  const idOf = (p) => (typeof p === 'object' ? p.id : p);
  const bIds = new Set(b.map(idOf));
  return a.filter(p => bIds.has(idOf(p)));
};

// Helper robusto para extraer la duración en minutos de un objeto Duration.
// El backend tiene varios shapes según el endpoint:
//   - Campaign API: a veces sólo `name` y nada más
//   - dais.specialty.get_model_data: `duration` en minutos
//   - Otras variantes que usan `duration_minutes`, `minutes`, `time`
// También maneja el caso donde `duration` venga en SEGUNDOS (> 300).
export const extractDurationMinutes = (d) => {
  if (!d || typeof d !== 'object') return null;
  const candidates = [
    d.durationMinutes,
    d.duration_minutes,
    d.minutes,
    d.duration,
    d.time,
    d.duration_in_minutes,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) {
      // Si el valor es absurdamente grande, asumimos que está en segundos.
      if (n > 300) return Math.round(n / 60);
      return n;
    }
  }
  // Fallback: parsear desde el `name` si tiene formato "60 min".
  const name = typeof d.name === 'string' ? d.name : '';
  const m = name.match(/(\d+)\s*min/i);
  if (m) return parseInt(m[1], 10);
  return null;
};

// Convenience: ordena duraciones de mayor a menor (estable).
// El UI debe preseleccionar la larga.
export const sortDurationsLongFirst = (durations = []) => {
  if (!Array.isArray(durations)) return [];
  return [...durations].sort((a, b) => {
    const da = Number(a?.duration ?? a?.durationSeconds ?? 0);
    const db = Number(b?.duration ?? b?.durationSeconds ?? 0);
    return db - da;
  });
};

// Helper para detección on-demand desde response de booking.
export const isOnDemandResponse = (responseData) => {
  if (!responseData || typeof responseData !== 'object') return false;
  if (responseData.requires_doctor_confirmation === true) return true;
  if (responseData.state === 'pending_doctor_confirmation') return true;
  return false;
};
